import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { passengerAdmin, getAdminConfigWarning, readBases } from "../components/services/adminBackendFetch";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { formatAdminDateTime } from "../utils/formatAdminDateTime";
import { useAppContext } from "../providers/ProviderAppContainer";

function money(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
}

function percent(rate) {
    if (rate == null || rate === "") return "—";
    if (typeof rate === "string" && /[-–]/.test(rate) && !/^\d+(\.\d+)?$/.test(rate.trim())) {
        return rate
            .split(/[-–]/)
            .map((part) => percent(part.trim()))
            .join(" – ");
    }
    const v = Number(rate);
    if (!Number.isFinite(v)) return "—";
    const pct = v <= 1 ? v * 100 : v;
    return `${Math.round(pct * 100) / 100}%`;
}

function toDateOnly(d) {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function shortDate(value) {
    if (!value) return "—";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    const day = String(dt.getDate()).padStart(2, "0");
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const year = String(dt.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
}

function downloadBlob(filename, blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function escCsv(v) {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

/** Flat trip rows for CSV (same columns as server export). */
function driversToCsv(drivers) {
    const headers = [
        "driver_id",
        "driver_name",
        "driver_phone",
        "vehicle_number",
        "driver_rate",
        "driver_total_payout",
        "driver_total_commission",
        "driver_total_tip",
        "receipt_number",
        "ride_id",
        "dated",
        "passenger_total",
        "trip_driver_rate",
        "platform_commission",
        "tip",
        "driver_payout",
        "paid",
        "paid_at",
    ];
    const lines = [headers.join(",")];
    for (const d of drivers || []) {
        for (const t of d.trips || []) {
            lines.push(
                [
                    d.driver_id,
                    d.driver_name,
                    d.driver_phone,
                    d.vehicle_number,
                    d.commission_rate,
                    d.total_payout,
                    d.total_commission,
                    d.total_tip,
                    t.receipt_number,
                    t.id,
                    t.dated,
                    t.passenger_total ?? t.price,
                    t.commission_rate,
                    t.commission,
                    t.tip,
                    t.payout,
                    t.paid ? 1 : 0,
                    t.paid_at,
                ]
                    .map(escCsv)
                    .join(","),
            );
        }
    }
    return `${lines.join("\n")}\n`;
}

const PAID_ALL = "all";

const PAID_FILTER_OPTIONS = [
    { label: "All trips", value: PAID_ALL },
    { label: "Unpaid only", value: "false" },
    { label: "Paid only", value: "true" },
];

function isAllPaidFilter(value) {
    return value == null || value === "" || value === PAID_ALL;
}

function SummaryStat({ label, value, hint, accent }) {
    return (
        <div
            className="border-round-lg px-3 py-2 flex flex-column gap-1 min-w-0"
            style={{
                background: accent === "payout" ? "var(--green-50)" : accent === "commission" ? "var(--blue-50)" : "var(--surface-ground)",
                border: "1px solid var(--surface-border)",
                minWidth: "9.5rem",
            }}
        >
            <span className="text-xs text-600 font-medium uppercase" style={{ letterSpacing: "0.02em" }}>
                {label}
            </span>
            <span className="text-lg font-bold text-900 white-space-nowrap">{value}</span>
            {hint ? <span className="text-xs text-500 line-height-2">{hint}</span> : null}
        </div>
    );
}

export default function WithdrawalRevenuePage() {
    const { showToast } = useAppContext();
    const configWarning = getAdminConfigWarning();
    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(null);
    const [error, setError] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [meta, setMeta] = useState({ total_drivers: 0, total_trips: 0 });
    const [from, setFrom] = useState(null);
    const [to, setTo] = useState(null);
    const [paidFilter, setPaidFilter] = useState("false");
    const [expanded, setExpanded] = useState({});
    const [exporting, setExporting] = useState(false);

    const buildQuery = useCallback(() => {
        const qs = new URLSearchParams();
        const fromDay = toDateOnly(from);
        const toDay = toDateOnly(to);
        if (fromDay) qs.set("from", fromDay);
        if (toDay) qs.set("to", toDay);
        if (!isAllPaidFilter(paidFilter)) qs.set("paid", paidFilter);
        return qs;
    }, [from, to, paidFilter]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await passengerAdmin(`/admin/reports/withdrawals?${buildQuery().toString()}`);
            const list = Array.isArray(data?.drivers) ? data.drivers : [];
            setDrivers(list);
            setMeta({
                total_drivers: Number(data?.total_drivers) || 0,
                total_trips: Number(data?.total_trips) || 0,
            });
            setExpanded((prev) => {
                const next = { ...prev };
                for (const d of list) {
                    if (next[d.driver_id] === undefined) next[d.driver_id] = true;
                }
                return next;
            });
        } catch (e) {
            setError(e?.message || String(e));
            setDrivers([]);
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    useEffect(() => {
        if (configWarning) {
            setLoading(false);
            return;
        }
        load();
    }, [configWarning, load]);

    const pageTotals = useMemo(() => {
        return drivers.reduce(
            (acc, d) => {
                acc.payout += Number(d.total_payout) || 0;
                acc.commission += Number(d.total_commission) || 0;
                acc.tip += Number(d.total_tip) || 0;
                return acc;
            },
            { payout: 0, commission: 0, tip: 0 },
        );
    }, [drivers]);

    async function handleExportCsv() {
        if (exporting) return;
        setExporting(true);
        setError(null);
        try {
            const qs = buildQuery();
            qs.set("format", "csv");
            let tripCount = meta.total_trips;
            try {
                const { passengerBase } = readBases();
                const url = `${passengerBase}/admin/reports/withdrawals/export?${qs.toString()}`;
                const res = await fetch(url);
                if (!res.ok) throw new Error(`Export failed (${res.status})`);
                const blob = await res.blob();
                downloadBlob("swyft-withdrawal-revenue.csv", blob);
            } catch {
                // Fallback: export currently loaded rows in the browser.
                const csv = driversToCsv(drivers);
                tripCount = drivers.reduce((n, d) => n + (d.trips?.length || 0), 0);
                downloadBlob(
                    "swyft-withdrawal-revenue.csv",
                    new Blob([csv], { type: "text/csv;charset=utf-8" }),
                );
            }
            showToast?.({
                severity: "success",
                summary: "Export ready",
                detail: `${tripCount} trip row(s) exported as CSV.`,
            });
        } catch (e) {
            setError(e?.message || String(e));
            showToast?.({
                severity: "error",
                summary: "Export failed",
                detail: e?.message || String(e),
            });
        } finally {
            setExporting(false);
        }
    }

    function markDriverPaid(driver) {
        if (!driver?.driver_id || driver.all_paid) return;
        const unpaidIds = (driver.trips || []).filter((t) => !t.paid).map((t) => t.id);
        if (unpaidIds.length === 0) return;

        confirmDialog({
            message: `Mark ${unpaidIds.length} trip(s) for ${driver.driver_name} as paid out (${money(driver.total_payout)} total in this range)?`,
            header: "Confirm payout",
            icon: "pi pi-wallet",
            acceptLabel: "Mark paid",
            rejectLabel: "Cancel",
            accept: async () => {
                setMarking(driver.driver_id);
                setError(null);
                try {
                    await passengerAdmin(`/admin/reports/withdrawals/mark-paid`, {
                        method: "POST",
                        body: JSON.stringify({
                            driver_id: driver.driver_id,
                            from: toDateOnly(from) || undefined,
                            to: toDateOnly(to) || undefined,
                        }),
                    });
                    showToast?.({
                        severity: "success",
                        summary: "Marked paid",
                        detail: `${driver.driver_name} — ${unpaidIds.length} trip(s)`,
                    });
                    await load();
                } catch (e) {
                    setError(e?.message || String(e));
                    showToast?.({
                        severity: "error",
                        summary: "Mark paid failed",
                        detail: e?.message || String(e),
                    });
                } finally {
                    setMarking(null);
                }
            },
        });
    }

    if (configWarning) {
        return <Message severity="warn" text={configWarning} className="w-full" />;
    }

    return (
        <div>
            <ConfirmDialog />
            <AdminPageHeader
                title="Withdrawal & Revenue"
                subtitle="Driver payouts for completed trips. Filter by date, then export CSV or mark driver payouts as paid."
            />
            {error ? <Message severity="error" text={error} className="w-full mb-3" /> : null}

            <div className="surface-card border-round-xl p-3 mb-3">
                <div className="grid align-items-end">
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">Start date</label>
                        <Calendar
                            className="w-full"
                            value={from}
                            onChange={(e) => setFrom(e.value)}
                            dateFormat="yy-mm-dd"
                            showIcon
                            placeholder="Start date"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">End date</label>
                        <Calendar
                            className="w-full"
                            value={to}
                            onChange={(e) => setTo(e.value)}
                            dateFormat="yy-mm-dd"
                            showIcon
                            placeholder="End date"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block text-sm font-medium mb-2">Paid status</label>
                        <Dropdown
                            className="w-full"
                            value={paidFilter ?? PAID_ALL}
                            options={PAID_FILTER_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setPaidFilter(e.value ?? PAID_ALL)}
                        />
                    </div>
                    <div className="col-12 md:col-4 flex flex-wrap gap-2">
                        <Button label="Apply" icon="pi pi-filter" onClick={load} disabled={loading || exporting} />
                        <Button
                            label="Clear"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            onClick={() => {
                                setFrom(null);
                                setTo(null);
                                setPaidFilter("false");
                            }}
                            disabled={loading || exporting}
                        />
                        <Button
                            label="Export CSV"
                            icon="pi pi-download"
                            severity="success"
                            outlined
                            loading={exporting}
                            disabled={loading || (!drivers.length && !exporting)}
                            onClick={handleExportCsv}
                        />
                    </div>
                </div>
                <div className="flex flex-wrap align-items-center gap-3 mt-3 text-sm text-600">
                    <span>
                        {meta.total_drivers} driver(s) · {meta.total_trips} trip(s)
                    </span>
                    {!loading && drivers.length > 0 ? (
                        <>
                            <span>·</span>
                            <span>
                                Page payout <strong className="text-900">{money(pageTotals.payout)}</strong>
                            </span>
                            <span>
                                Page commission{" "}
                                <strong className="text-900">{money(pageTotals.commission)}</strong>
                            </span>
                        </>
                    ) : null}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center py-6">
                    <ProgressSpinner style={{ width: "40px", height: "40px" }} />
                </div>
            ) : drivers.length === 0 ? (
                <Message severity="info" text="No completed trips found for this range." className="w-full" />
            ) : (
                <div className="flex flex-column gap-3">
                    {drivers.map((driver) => {
                        const busy = marking === driver.driver_id;
                        const isOpen = expanded[driver.driver_id] !== false;
                        const rateLabel =
                            driver.commission_rate != null
                                ? percent(driver.commission_rate)
                                : driver.commission_rate_label != null
                                  ? percent(driver.commission_rate_label)
                                  : "—";
                        const rateHint = driver.rate_mixed
                            ? driver.rate_note
                                ? `Most trips use this rate. ${driver.rate_note}`
                                : "Most trips use this rate; a few trips differ (see trip table)."
                            : "Commission rate used on trips";

                        return (
                            <div
                                key={driver.driver_id}
                                className="surface-card border-round-xl border-1 surface-border overflow-hidden"
                            >
                                <div className="p-3">
                                    <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap align-items-center gap-2 mb-1">
                                                <h3 className="m-0 text-xl font-semibold text-900">
                                                    {driver.driver_name}
                                                </h3>
                                                {driver.all_paid ? (
                                                    <Tag value="Paid out" severity="success" />
                                                ) : (
                                                    <Tag
                                                        value={`${driver.unpaid_count} unpaid`}
                                                        severity="warning"
                                                    />
                                                )}
                                            </div>
                                            <div className="text-sm text-600">
                                                {driver.driver_phone || "No phone"}
                                                {driver.vehicle_number ? ` · ${driver.vehicle_number}` : ""}
                                                {` · ${driver.trip_count} trip(s)`}
                                            </div>
                                            <div className="text-sm text-700 mt-2">
                                                Driver share rate:{" "}
                                                <strong className="text-900">{rateLabel}</strong>
                                                <span className="text-500">
                                                    {" "}
                                                    (of fare before tip; city fee deducted from that share)
                                                </span>
                                                {driver.rate_mixed ? (
                                                    <div className="text-xs text-orange-600 mt-1">
                                                        Mixed rates in this range — card shows the most-used rate.
                                                        Per-trip rate is in the table.
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div className="flex align-items-center gap-3 flex-wrap">
                                            <div className="flex align-items-center gap-2 surface-ground border-round-lg px-3 py-2 border-1 surface-border">
                                                <Checkbox
                                                    inputId={`paid-${driver.driver_id}`}
                                                    checked={Boolean(driver.all_paid)}
                                                    disabled={busy || driver.all_paid}
                                                    onChange={() => markDriverPaid(driver)}
                                                />
                                                <label
                                                    htmlFor={`paid-${driver.driver_id}`}
                                                    className="text-sm font-semibold cursor-pointer select-none m-0"
                                                >
                                                    {busy ? "Saving…" : "Mark paid"}
                                                </label>
                                            </div>
                                            <Button
                                                type="button"
                                                text
                                                size="small"
                                                icon={isOpen ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                                                label={isOpen ? "Hide trips" : "Show trips"}
                                                onClick={() =>
                                                    setExpanded((prev) => ({
                                                        ...prev,
                                                        [driver.driver_id]: !isOpen,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <SummaryStat
                                            label="Total payout"
                                            value={money(driver.total_payout)}
                                            hint="Driver owed for this range"
                                            accent="payout"
                                        />
                                        <SummaryStat
                                            label="Total commission"
                                            value={money(driver.total_commission)}
                                            hint="Platform / company take"
                                            accent="commission"
                                        />
                                        <SummaryStat
                                            label="Total tips"
                                            value={money(driver.total_tip)}
                                            hint="Passenger tips charged"
                                        />
                                        <SummaryStat
                                            label="Driver rate"
                                            value={rateLabel}
                                            hint={rateHint}
                                        />
                                    </div>
                                </div>

                                {isOpen ? (
                                    <div className="border-top-1 surface-border">
                                        <DataTable
                                            value={driver.trips || []}
                                            size="small"
                                            stripedRows
                                            emptyMessage="No trips"
                                            className="text-sm"
                                        >
                                            <Column
                                                header="Receipt"
                                                body={(t) => (
                                                    <span className="font-medium">
                                                        {t.receipt_number || `#${t.id}`}
                                                    </span>
                                                )}
                                                style={{ minWidth: "7rem" }}
                                            />
                                            <Column
                                                header="Date"
                                                body={(t) => shortDate(t.dated)}
                                                style={{ minWidth: "6rem" }}
                                            />
                                            <Column
                                                header="Passenger paid"
                                                body={(t) => money(t.passenger_total ?? t.price)}
                                                style={{ minWidth: "8rem" }}
                                            />
                                            <Column
                                                header="Driver rate"
                                                body={(t) => percent(t.commission_rate)}
                                                style={{ minWidth: "7rem" }}
                                            />
                                            <Column
                                                header="Platform commission"
                                                body={(t) => money(t.commission)}
                                                style={{ minWidth: "9rem" }}
                                            />
                                            <Column
                                                header="Tip"
                                                body={(t) => money(t.tip)}
                                                style={{ minWidth: "6rem" }}
                                            />
                                            <Column
                                                header="Driver payout"
                                                body={(t) => (
                                                    <span className="font-semibold">{money(t.payout)}</span>
                                                )}
                                                style={{ minWidth: "8rem" }}
                                            />
                                            <Column
                                                header="Status"
                                                body={(t) =>
                                                    t.paid ? (
                                                        <Tag
                                                            value={
                                                                t.paid_at
                                                                    ? `Paid ${formatAdminDateTime(t.paid_at)}`
                                                                    : "Paid"
                                                            }
                                                            severity="success"
                                                        />
                                                    ) : (
                                                        <Tag value="Unpaid" severity="warning" />
                                                    )
                                                }
                                                style={{ minWidth: "9rem" }}
                                            />
                                        </DataTable>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
